import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { TRANSLATIONS, REASON_I18N } from "@/i18n/translations";

const STORAGE_KEY = "flex.lang";
const I18nContext = createContext(null);

function detectInitial() {
  if (typeof window === "undefined") return "es";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "es" || stored === "en") return stored;
  // Respect the full browser language preference chain (navigator.languages)
  const chain = (window.navigator?.languages && window.navigator.languages.length
    ? window.navigator.languages
    : [window.navigator?.language || "es"]);
  for (const raw of chain) {
    const code = (raw || "").slice(0, 2).toLowerCase();
    if (code === "es") return "es";
    if (code === "en") return "en";
  }
  return "es";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((l) => {
    if (l === "es" || l === "en") setLangState(l);
  }, []);

  const toggle = useCallback(() => setLangState((l) => (l === "es" ? "en" : "es")), []);

  const t = useCallback(
    (key, fallback) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.es;
      return dict[key] ?? fallback ?? key;
    },
    [lang]
  );

  const tReason = useCallback(
    (code, fallback) => REASON_I18N[code]?.[lang] || fallback || code,
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggle, t, tReason }), [lang, setLang, toggle, t, tReason]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback so components don't crash if a provider is missing.
    return {
      lang: "es",
      setLang: () => {},
      toggle: () => {},
      t: (k, fb) => fb || k,
      tReason: (c, fb) => fb || c,
    };
  }
  return ctx;
};
